// Качество данных: типы ответа ядра и подписи для человека. Только оформление —
// ни одного числа здесь не считается.
//
// ПОЧЕМУ ЗДЕСЬ НЕТ НИ ОДНОГО СЛОВАРЯ «КОД ПРОВЕРКИ → ПОДПИСЬ». Названия проверок, их
// объяснения, места починки и степени приезжают ИЗ ЯДРА вместе с числами (dq_api.py:
// _паспорт). Свой словарь пришлось бы держать второй копией, и он молча опустел бы в
// день, когда в ядре появится пятьдесят четвёртая проверка: строка в таблице осталась
// бы, а подпись у неё исчезла. Тот же приём, что на экране расхождений состава.
//
// ЧТО ЗДЕСЬ ЕСТЬ. Только то, чего ядру знать незачем: как назвать тренд по-русски, как
// показать «не знаем» так, чтобы его не приняли за «ровно», и как сказать «столько-то из
// стольких-то» одной строкой. Живую пару подставляет ядро — здесь только оправа.

/** Степень: чинить / посмотреть / так ведут учёт. Те же три, что в products_api. */
export type Степень = 'error' | 'check' | 'ok'

/** Куда движется число. 'unknown' — сравнить не с чем, и это НЕ 'flat'. */
export type Тренд = 'up' | 'down' | 'flat' | 'new' | 'none' | 'unknown'

export interface Место {
  label: string
  who: string
  why: string
}

/** Устройство проверки без чисел — приезжает и в /refs, и рядом с числами. */
export interface ПаспортПроверки {
  code: string
  group: string
  title: string
  what: string
  severity: Степень
  fix: string
  fix_label: string
  unit: string
  has_money: boolean
  currency: string | null
  dated: boolean
  date_note: string
  scope_note: string
  goto: string | null
  link: string | null
  note: string
}

/** Проверка вместе с числами. Все числовые поля могут быть null — см. `failed`. */
export interface Проверка extends ПаспортПроверки {
  n: number | null
  money: number | null
  n_new: number | null
  n_old: number | null
  n_nodate: number | null
  last_seen: string | null
  money_new: number | null
  scope?: number | null
  trend: Тренд
  trend_pct: number | null
  /** Текст ошибки, если проверка не выполнилась. null — выполнилась. */
  failed: string | null
}

export interface КучаПоСтепени {
  severity: Степень
  checks: number
  n: number
  failed: number
}

export interface КучаПоМесту {
  fix: string
  fix_label: string
  checks: number
  n: number
  failed: number
}

export interface Свежесть {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

export interface ОтветПроверок {
  checks: Проверка[]
  totals: { by_severity: КучаПоСтепени[]; by_fix: КучаПоМесту[] }
  groups: { code: string; title: string }[]
  places: Record<string, Место>
  places_order: string[]
  severities: Record<string, { label: string; what: string }>
  severity_order: Степень[]
  window_days: number
  trend_pct: number
  cached_sec: number
  cache_sec: number
  synced: Свежесть[]
  data_notes: Record<string, string>
}

export interface ОтветСтрок {
  check: ПаспортПроверки
  rows: Record<string, unknown>[]
  total: number
  limit: number
  start: number
  next: number | null
  data_notes: Record<string, string>
}

// ─────────────────────────── подписи ───────────────────────────

/**
 * Степень словами — на случай, если ядро её не прислало.
 *
 * Это НЕ второй словарь подписей: обычно берётся `severities` из ответа, а сюда экран
 * заглядывает только когда ответа ещё нет (кнопки фильтра рисуются до загрузки).
 * Разъехаться они не могут: смысл трёх степеней зафиксирован тестом ядра.
 */
export const ЗАПАСНАЯ_ПОДПИСЬ: Record<Степень, string> = {
  error: 'Чинить',
  check: 'Посмотреть',
  ok: 'Так ведут учёт',
}

/** Класс значка. Красный — только «чинить»: если красным горит всё, красный не значит ничего. */
export function классСтепени(с: Степень): string {
  if (с === 'error') return 'lose'
  if (с === 'ok') return 'won'
  return 'work'
}

/**
 * Тренд словами. Отдельная фраза на каждый случай, и это главное в файле.
 *
 * «Не знаем» и «ровно» — разные ответы, и подменять первое вторым нельзя: экран, который
 * пишет «ровно» там, где даты у записи нет вовсе, сообщает факт, которого не проверял.
 */
export function трендСловами(п: Проверка, окно: number): string {
  if (!п.dated) return 'динамику считать нечем: даты у записи нет';
  switch (п.trend) {
    case 'unknown':
      return 'сравнить не с чем';
    case 'none':
      return `за ${окно * 2} дн. таких записей не появлялось`;
    case 'new':
      return `появилось за последние ${окно} дн.: раньше не было ни одной`;
    case 'flat':
      return 'держится ровно';
    case 'up':
      return `растёт${проценты(п.trend_pct)}`;
    case 'down':
      return `убывает${проценты(п.trend_pct)}`;
    default:
      return 'сравнить не с чем';
  }
}

/** Короткая пометка для узкой колонки: стрелка и процент. */
export function трендЗначком(п: Проверка): string {
  if (!п.dated) return '—';
  switch (п.trend) {
    case 'up': return `↑${проценты(п.trend_pct, true)}`;
    case 'down': return `↓${проценты(п.trend_pct, true)}`;
    case 'flat': return '→ ровно';
    case 'new': return '↑ новое';
    case 'none': return 'нет свежих';
    default: return '—';
  }
}

function проценты(доля: number | null, коротко = false): string {
  if (доля === null || доля === undefined) return '';
  const n = Math.round(Math.abs(доля) * 100);
  // Рост «в разы» процентами не читается: «+1951 %» — это шум, а не число.
  if (n >= 300) {
    const разы = Math.round(Math.abs(доля) + 1);
    return коротко ? ` в ${разы}×` : ` — в ${разы} раза больше, чем окном раньше`;
  }
  return коротко ? ` ${n}%` : ` на ${n} %`;
}

/**
 * «30 из 31 467 счетов клиенту» (на 28.07.2026). Знаменатель важнее, чем кажется: одно
 * и то же число означает разное в зависимости от того, из скольких оно.
 */
export function сколько(п: Проверка): string {
  if (п.n === null) return 'не посчитано';
  const своё = п.n.toLocaleString('ru-RU');
  if (п.scope === null || п.scope === undefined || п.scope === 0) return `${своё} ${п.unit}`;
  const доля = Math.round((п.n / п.scope) * 1000) / 10;
  return `${своё} ${п.unit} из ${п.scope.toLocaleString('ru-RU')}`
    + (доля >= 0.1 ? ` (${доля.toString().replace('.', ',')} %)` : '');
}

/**
 * Возраст ответа словами. Пишем ВСЕГДА, даже когда он нулевой: ответ ядра держится
 * в кэше несколько минут, и молча показывать позавчерашнее число — то же враньё,
 * только тише.
 */
export function когдаПосчитано(сек: number): string {
  const c = Number.isFinite(сек) && сек > 0 ? Math.round(сек) : 0;
  if (c < 60) return 'посчитано только что';
  const мин = Math.round(c / 60);
  return `посчитано ${мин} ${окончание(мин, 'минуту', 'минуты', 'минут')} назад`;
}

/** «1 минуту», «2 минуты», «5 минут» — иначе получается «5 минуту». */
function окончание(n: number, один: string, мало: string, много: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return много;
  if (b === 1) return один;
  if (b >= 2 && b <= 4) return мало;
  return много;
}

/**
 * Отбор проверок для показа. Пустое значение фильтра — «не сужать».
 *
 * Считается ЗДЕСЬ, а не в ядре, намеренно: ядро отдаёт все проверки одним ответом
 * (их полсотни, это несколько килобайт), и фильтр не должен стоить обращения к сети.
 */
export interface Отбор {
  severity?: string
  fix?: string
  group?: string
  q?: string
  /** Прятать проверки, у которых нашёлся ноль записей. */
  hideEmpty?: boolean
}

export function отобрать(проверки: Проверка[], о: Отбор): Проверка[] {
  const текст = (о.q ?? '').trim().toLowerCase();
  return проверки.filter((п) => {
    if (о.severity && п.severity !== о.severity) return false;
    if (о.fix && п.fix !== о.fix) return false;
    if (о.group && п.group !== о.group) return false;
    // Упавшую проверку не прячем НИКОГДА: «ноль» и «не посчитали» — разные ответы,
    // и спрятать второй значит показать чистый экран там, где мы просто не знаем.
    if (о.hideEmpty && п.n === 0 && !п.failed) return false;
    if (текст && !`${п.title} ${п.what} ${п.code} ${п.fix_label}`.toLowerCase()
      .includes(текст)) return false;
    return true;
  });
}

/** Порядок строк: сначала то, что чинить, внутри — по числу записей. */
export const ВЕС_СТЕПЕНИ: Record<Степень, number> = { error: 0, check: 1, ok: 2 };

export function поВажности(a: Проверка, b: Проверка): number {
  // Упавшие — наверх: строка «не посчитано» важнее любого числа, потому что она
  // означает, что экран сейчас неполон.
  if (!!a.failed !== !!b.failed) return a.failed ? -1 : 1;
  const с = ВЕС_СТЕПЕНИ[a.severity] - ВЕС_СТЕПЕНИ[b.severity];
  if (с !== 0) return с;
  return (b.n ?? -1) - (a.n ?? -1);
}
